import React, { useState } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Loader2, AlertCircle } from 'lucide-react';

const TYPES = ['Individual', 'Sole trader', 'Company', 'Partnership', 'Trust'];
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// A broker's client who is not in the register yet — a person or a business; the ABN is optional.
export const NewClientForm = ({ initialName = '', initial = null, onCreated, onCancel }) => {
  const api = useApi();
  const [form, setForm] = useState({ legal_name: initialName, entity_type: 'Individual', abn: '', industry_code: '', suburb: '', state: 'VIC', postcode: '', ...(initial || {}) });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isPerson = form.entity_type === 'Individual' || form.entity_type === 'Sole trader';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await api.post('/entities', { ...form, abn: (form.abn || '').trim() || null, city: form.suburb });
      onCreated(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not add this client.');
    }
    setSaving(false);
  };

  return (
    <Card className="p-4 rounded-sm border bg-white" data-testid="new-client-form">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus className="w-4 h-4" style={{ color: '#1A4C7C' }} />
        <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Add a new client</span>
        <span className="text-xs" style={{ color: '#5C6B7A' }}>— a person or a business; ABN only if they have one</span>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{isPerson ? 'Full name' : 'Legal name'}</Label>
          <Input data-testid="new-client-name" value={form.legal_name} onChange={set('legal_name')} required minLength={3} className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Client type</Label>
          <select data-testid="new-client-type" value={form.entity_type} onChange={set('entity_type')}
            className="mt-1 w-full h-10 rounded-sm border border-slate-300 bg-white px-3 text-sm">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>ABN <span className="normal-case tracking-normal">(optional)</span></Label>
          <Input data-testid="new-client-abn" value={form.abn} onChange={set('abn')} placeholder={isPerson ? 'Leave blank if none' : '11 digits'} className="mt-1 rounded-sm" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{isPerson ? 'Occupation / activity' : 'Industry'}</Label>
          <Input data-testid="new-client-industry" value={form.industry_code} onChange={set('industry_code')} placeholder={isPerson ? 'e.g. Electrician, Landlord' : 'e.g. Cafe, Hardware retailing'} className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Suburb</Label>
          <Input data-testid="new-client-suburb" value={form.suburb} onChange={set('suburb')} className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>State</Label>
          <select data-testid="new-client-state" value={form.state} onChange={set('state')}
            className="mt-1 w-full h-10 rounded-sm border border-slate-300 bg-white px-3 text-sm">
            {STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Postcode</Label>
          <Input data-testid="new-client-postcode" value={form.postcode} onChange={set('postcode')} required pattern="\d{4}" className="mt-1 rounded-sm" />
        </div>
        {error && (
          <div className="md:col-span-3 flex items-center gap-2 text-sm p-3 rounded-sm" style={{ background: 'rgba(226,75,74,0.12)', color: '#B03230' }} data-testid="new-client-error">
            <AlertCircle className="w-4 h-4 shrink-0" /><span>{error}</span>
          </div>
        )}
        <div className="md:col-span-3 flex items-center gap-2 pt-1">
          <Button data-testid="new-client-submit" type="submit" disabled={saving} className="rounded-sm text-white" style={{ background: '#1B4A9A' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add client and continue'}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="rounded-sm text-xs" data-testid="new-client-cancel">Cancel</Button>
        </div>
      </form>
    </Card>
  );
};
